import React, {useEffect, useRef, useState} from "react"

import PropTypes from "prop-types"
import {Menu, Popup, Ref} from "semantic-ui-react"

import {useIsResponsive} from "../../hooks/useIsResponsive"
import {useTexts} from "../../hooks/useTexts"
import {isEscapeKeyEvent, isKeyboardClickEvent} from "../../utils/accessibilityUtil"
import {getDataTestId} from "../../utils/stringUtil"
import HeaderLink from "../HeaderLink/headerLink"

import "./headerMenuPopup.scss"

const HeaderMenuPopup = ({isConnected = false, menuLinks = [], textColor, username}) => {
    const isResponsive = useIsResponsive()
    const texts = useTexts()
    const [isOpen, setIsOpen] = useState(false)
    const menuLinkRef = useRef()
    const popupRef = useRef()
    const menuRef = useRef()

    // Gestion des clics à l'exterieur de la popup
    useEffect(() => {
        function handleClickOutside(event) {
            if (
                isOpen &&
                popupRef.current &&
                !popupRef.current.contains(event.target) &&
                !menuLinkRef.current?.contains(event.target)
            ) {
                setIsOpen(false)
            }
        }

        function handleEscapeKey(event) {
            if (isEscapeKeyEvent(event) && isOpen) {
                setIsOpen(false)
                menuLinkRef.current?.focus()
            }
        }

        document.addEventListener("mousedown", handleClickOutside)
        document.addEventListener("keydown", handleEscapeKey)
        return () => {
            document.removeEventListener("mousedown", handleClickOutside)
            document.removeEventListener("keydown", handleEscapeKey)
        }
    }, [isOpen])

    // A l'ouverture de la popup de menu, le focus est mis sur le premier item
    useEffect(() => {
        if (isOpen && menuRef.current) {
            let attempts = 0
            const maxAttempts = 10
            const focusFirstItem = () => {
                const firstMenuItem = menuRef.current?.querySelector('[role="menuitem"][tabindex="0"]')
                if (firstMenuItem) {
                    firstMenuItem.focus()
                } else if (attempts < maxAttempts) {
                    attempts++
                    requestAnimationFrame(focusFirstItem)
                }
            }
            requestAnimationFrame(focusFirstItem)
        }
    }, [isOpen])

    // Gestion des events au clavier sur les compos HeaderLink
    const handleHeaderLinkKeyDown = event => {
        if (isKeyboardClickEvent(event)) {
            event.preventDefault()
            setIsOpen(!isOpen)
        }
    }

    // Gestion des events au clavier sur les items du menu
    const handleMenuItemKeyDown = (event, linkProps) => {
        if (!menuRef.current) {
            return
        }

        if (isKeyboardClickEvent(event)) {
            event.preventDefault()
            setIsOpen(false)
            linkProps.onClick()
            return
        }

        const menuItems = Array.from(menuRef.current.querySelectorAll('[role="menuitem"][tabindex="0"]'))
        const currentIndex = menuItems.findIndex(item => item === event.currentTarget)

        switch (event.key) {
            case "ArrowDown":
                event.preventDefault()
                if (currentIndex < menuItems.length - 1) {
                    menuItems[currentIndex + 1]?.focus()
                } else {
                    menuItems[0]?.focus()
                }
                break
            case "ArrowUp":
                event.preventDefault()
                if (currentIndex > 0) {
                    menuItems[currentIndex - 1]?.focus()
                } else {
                    menuItems[menuItems.length - 1]?.focus()
                }
                break
            default:
                break
        }
    }

    const ariaLabel = isOpen ? "Fermer le menu de navigation" : "Ouvrir le menu de navigation"

    return (
        <>
            {isOpen && isResponsive && <div aria-hidden="true" className="header-menu-popup-background" />}

            <Ref innerRef={menuLinkRef}>
                <HeaderLink
                    aria-expanded={isOpen}
                    aria-haspopup="true"
                    aria-label={ariaLabel}
                    data-testid="lienUsername"
                    icon="fa-solid fa-circle-user"
                    label={isConnected && username ? username : texts.headerLogin}
                    onClick={() => setIsOpen(!isOpen)}
                    onKeyDown={handleHeaderLinkKeyDown}
                    tabIndex="0"
                    textColor={textColor}
                    withArrowIcon={!isResponsive}
                />
            </Ref>

            <Ref innerRef={popupRef}>
                <Popup
                    basic
                    className="header-menu-popup"
                    context={menuLinkRef}
                    offset={[0, -10]}
                    open={isOpen}
                    position="bottom right"
                >
                    <Ref innerRef={menuRef}>
                        <Menu role="menu" secondary vertical>
                            {isResponsive && isConnected && username && (
                                <Menu.Item content={username} header role="presentation" tabIndex="-1" />
                            )}
                            {menuLinks.map(linkProps => (
                                <Menu.Item
                                    data-testid={getDataTestId(linkProps.label, "lien")}
                                    key={`header-menu-popup-item-${linkProps.label}`}
                                    onClick={() => {
                                        setIsOpen(false)
                                        linkProps.onClick()
                                    }}
                                    onKeyDown={event => handleMenuItemKeyDown(event, linkProps)}
                                    role="menuitem"
                                    tabIndex="0"
                                >
                                    {linkProps.label}
                                </Menu.Item>
                            ))}
                        </Menu>
                    </Ref>
                </Popup>
            </Ref>
        </>
    )
}

HeaderMenuPopup.propTypes = {
    isConnected: PropTypes.bool,
    menuLinks: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string,
            onClick: PropTypes.func,
        }),
    ),
    textColor: PropTypes.string.isRequired,
    username: PropTypes.string,
}

export default HeaderMenuPopup
